<template>
  <div class="product-card" :class="{'draft': props.product.draft}">
    <div class="left-side-container">
      <div class="product-header">
        <div class="product-title">{{ props.product.draft ? 'En revisión: ' : '' }} {{ props.product.titulo }}</div>
      </div>
      
      <div class="product-date" v-if="props.product.fecha != ''">
        <b>Fecha:</b> {{ formatDate(props.product.fecha) }} a las {{ props.product.hora }}
      </div>
      <div class="product-date" v-else-if="props.fecha_validez != null">
        <b>Validez:</b> {{ formatDate(props.fecha_validez)}}
      </div>

      <div class="product-footer">
        <div class="product-amounts">
          <span>Plazas vendidas: <strong>{{ plazasVendidas }}</strong></span>
          <span style="margin-left: 12px;">Plazas totales: <strong>{{ props.product.stock_maximo }}</strong></span>
        </div>
      </div>
    </div>
    <div class="right-side-container">
      <span class="right-side-title">Plazas totales</span>
      <div class="buttons-container">
        <button type="button" class="qty-btn" :class="minusButtonClass" @click="decreaseQuantity">&minus;</button>
        <span class="quantity">{{ cantidad }}</span>
        <button type="button" class="qty-btn" :class="plusButtonClass" @click="increaseQuantity">&plus;</button>
      </div>
    </div>
    <div class="bottom-buttons-container">
      <button @click="addNewDateClicked" v-if="props.product.fecha != ''" class="secondary_button w-full" style="padding: 5px; margin-top: 5px;">Añadir nueva fecha</button>
      <button @click="setAsComplete" class="secondary_button w-full" :class="setAsCompleteButtonClass" style="padding: 5px; margin-top: 5px;">Marcar como completo</button>
    </div>
  </div>
</template>
  
<script setup>
  import { defineEmits, defineExpose, defineProps, ref, computed, watch } from 'vue';
  import { format, parse } from 'date-fns';
  import { es } from 'date-fns/locale';
  
  const props = defineProps({
    fecha_validez: String,
    product: Object,
  });
  
  const cantidad = ref(props.product?.stock_maximo || 0);
  const plazasVendidas = ref(Math.max(0, (props.product?.stock_maximo || 0) - (props.product?.stock_actual || 0)));

  let currentProductId = null;

  watch(
    () => props.product,
    (newProduct) => {
      if (newProduct && newProduct.id_producto_promo !== currentProductId) {
        currentProductId = newProduct.id_producto_promo;
        plazasVendidas.value = Math.max(0, (newProduct.stock_maximo || 0) - (newProduct.stock_actual || 0));
        cantidad.value = newProduct.stock_maximo || 0;
      }
    },
    { immediate: true }
  );

  const setAsCompleteButtonClass = computed(() => {
    const fechaInvalida = props.product.fecha !== '' && Date.parse(props.product.fecha) < Date.now();
    if (cantidad.value <= plazasVendidas.value || fechaInvalida) {
      return "button-disabled";
    } else {
      return "button-enabled";
    }
  });

  const minusButtonClass = computed(() => {
    const fechaInvalida = props.product.fecha !== '' && Date.parse(props.product.fecha) < Date.now();
    if (cantidad.value <= plazasVendidas.value || fechaInvalida) {
      return "button-disabled";
    } else {
      return "button-enabled";
    }
  });

  const plusButtonClass = computed(() => {
    const fechaInvalida = props.product.fecha !== '' && Date.parse(props.product.fecha) < Date.now();
    if (fechaInvalida) {
      return "button-disabled";
    } else {
      return "button-enabled";
    }
  });

  const emit = defineEmits(['update:quantity', 'add-new-date']);

  const decreaseQuantity = () => {
    const fechaInvalida = props.product.fecha !== '' && Date.parse(props.product.fecha) < Date.now();
    if (cantidad.value > plazasVendidas.value && !fechaInvalida) {
      cantidad.value--;
      emit('update:quantity', cantidad.value);
      console.log("Decreased quantity to: ", cantidad.value);
    }
  };

  const increaseQuantity = () => {
    const fechaInvalida = props.product.fecha !== '' && Date.parse(props.product.fecha) < Date.now();
    if (!fechaInvalida) {
      cantidad.value++;
      emit('update:quantity', cantidad.value);
      console.log("Increased quantity to: ", cantidad.value);
    }
  };
  
  const selectAllExecuted = () => {
    cantidad.value = props.product.cantidad - props.product.cantidad_canjeada;
    emit('update:quantity', cantidad.value);
    console.log("Set all executed to: ", cantidad.value);
  };

  defineExpose({ selectAllExecuted });

  const formatDate = (date) => {
    const fechaAux = parse(date, "yyyy-MM-dd", new Date());
    return format(fechaAux, "dd MMM yyyy", { locale: es });
  };

  const setAsComplete = () => {
    const fechaInvalida = props.product.fecha !== '' && Date.parse(props.product.fecha) < Date.now();
    if (cantidad.value > plazasVendidas.value && !fechaInvalida) {
      cantidad.value = plazasVendidas.value;
      emit('update:quantity', cantidad.value);
      console.log("Set as complete to: ", cantidad.value);
    }
  };

const addNewDateClicked = () => {
  emit('add-new-date', props.product);
};
  
</script>
  
<style scoped>  
.product-card {
  border-radius: var(--radius-md);
  padding: 1rem 1.25rem;
  margin-bottom: 0.85rem;
  border: 1.5px solid var(--border-color);
  transition: all var(--transition-fast);
  border-color: rgba(32, 205, 168, 0.4);
  background-color: rgba(32, 205, 168, 0.04);
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.draft {
  border-color: #ffd166 !important;
  background-color: #fffef4 !important;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
}

.product-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--text-color-dark);
}

.product-date {
  font-size: 0.825rem;
}

.product-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed var(--border-color-subtle);
}

.product-amounts {
  font-size: 0.825rem;
  color: var(--text-color-dark);
}

.left-side-container {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.right-side-container {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.right-side-title {
  font-size: 0.825rem;
  color: var(--text-color-light);
}

.buttons-container {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.bottom-buttons-container {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px
}

.qty-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid var(--border-color);
  background: var(--white);
  color: var(--text-color-dark);
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all var(--transition-fast);
  padding: 4px 0 0 0;
}

.qty-btn:hover:not(:disabled) {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.quantity {
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--text-color-dark);
  min-width: 24px;
  text-align: center;
}

.button-disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.button-enabled {
  opacity: 1;
}
</style>
  